import { HTTP_INTERCEPTORS, HttpEvent, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { environment as env } from 'src/environments/environment';
import { DataService } from '../shared/data.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private HOST = env.HOST;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private router: Router, private service: DataService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Clone request with current token
    //Important pour eviter une boucle infinie
    // Ne pas intercepter les requêtes hors API (adresses, etc.)
    if (!request.url.includes(this.HOST)) {
      return next.handle(request);
    }
    if (request.url.includes('/utilisateur/refresh')) {
      return next.handle(request);
    }
    const authReq = this.addToken(request);
    const exp = localStorage.getItem('token_exp');
    const currentUrl = this.router.url;
    const bypassRefresh = currentUrl.startsWith('/acces-client') || currentUrl === '/gestion' || currentUrl.startsWith('/gestion/');

    // If token expired, queue request until refreshed
    // Bypass le refresh si on est sur acces-client ou gestion
    if (!bypassRefresh && exp && Math.floor(Date.now() / 1000) > parseInt(exp, 10)) {
      return this.handle401Error(authReq, next);
    }

    // Otherwise, proceed and catch 401s
    return next.handle(authReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if (error.error?.admin && error.error?.unauthorized) {
            localStorage.removeItem('admin_logged_in');
            this.router.navigate(['gestion/login']);
            return EMPTY;
          }
          if (error.error?.client && error.error?.unauthorized) {
            localStorage.removeItem('client_logged_in');
            localStorage.removeItem('user_type');
            localStorage.removeItem('utilisateur');
            this.router.navigate(['acces-client/login']);
            return EMPTY;
          }

          return this.handle401Error(authReq, next);
        }
        return throwError(error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //ajouter cond ne pas rafraichir si pas backoffice
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);//On met les requetes en attente

      // Trigger token refresh
      return this.service.refreshToken().pipe(
        switchMap((cred: any) => {
          this.isRefreshing = false;
          if (!cred?.error) {
            const data = cred.data;
            localStorage.setItem('token', data.token);
            localStorage.setItem('token_exp', data.expire);
            this.service.refreshAccount();
            this.refreshTokenSubject.next(data.token);
            return next.handle(this.addToken(request));
          } else {
            this.refreshTokenSubject.next('FAILED');
            this.logout();
            return throwError(cred);
          }
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next('FAILED');
          this.logout();
          return throwError(err);
        })
      );
    }

    // If a refresh is already in progress, wait for it
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        if (token === 'FAILED') {
          return throwError('Session expirée');
        }
        return next.handle(this.addToken(request));
      })
    );
  }

  private addToken(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      withCredentials: true,
      headers: request.headers
        .set('Authorization', `Bearer ${localStorage.getItem('token')}`)
    });
  }

  private logout() {
    this.service.disconnect();
    this.router.navigate(['backoffice/login']);
  }
}

